import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface AuditRow {
  id: string;
  admin_user_id: string | null;
  action_type: string;
  target_table: string | null;
  target_record_id: string | null;
  reason: string | null;
  created_at: string;
}

export default function AdminPrivacyCompliance() {
  const { user } = useAuth();
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [counts, setCounts] = useState<{ children: number; pending_requests: number; revoked: number }>({ children: 0, pending_requests: 0, revoked: 0 });
  const [retention, setRetention] = useState<Array<{ data_category: string; retention_days: number }>>([]);

  useEffect(() => {
    const load = async () => {
      const [a, ch, req, rev, ret] = await Promise.all([
        supabase.from("admin_audit_logs").select("*").order("created_at", { ascending: false }).limit(100),
        supabase.from("child_profiles").select("id", { count: "exact", head: true }),
        supabase.from("child_data_requests").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("child_profiles").select("id", { count: "exact", head: true }).eq("consent_status", "revoked"),
        supabase.from("data_retention_policies").select("data_category, retention_days"),
      ]);
      setAudit((a.data ?? []) as AuditRow[]);
      setCounts({
        children: ch.count ?? 0,
        pending_requests: req.count ?? 0,
        revoked: rev.count ?? 0,
      });
      setRetention((ret.data ?? []) as Array<{ data_category: string; retention_days: number }>);
      if (user) {
        try {
          await supabase.rpc("log_admin_access" as never, {
            _action_type: "view_privacy_dashboard",
            _target_table: "admin_audit_logs",
            _target_record_id: null,
            _reason: "dashboard_view",
          } as never);
        } catch { /* non-fatal */ }
      }
    };
    void load();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [user?.id]);

  return (
    <div className="min-h-screen bg-background p-6 max-w-5xl mx-auto">
      <Helmet>
        <title>Privacy Compliance | HeirWise Admin</title>
        <meta name="description" content="Internal child-privacy compliance dashboard: data requests, retention windows, and audit logs." />
      </Helmet>
      <h1 className="text-3xl font-heading font-bold mb-6">Privacy Compliance</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Stat label="Child profiles" value={counts.children} />
        <Stat label="Pending requests" value={counts.pending_requests} />
        <Stat label="Revoked consent" value={counts.revoked} />
      </div>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-3">Retention windows</h2>
        <div className="rounded-2xl border border-border bg-card/60 divide-y divide-border">
          {retention.map((r) => (
            <div key={r.data_category} className="px-4 py-2 flex justify-between text-sm">
              <span className="capitalize">{r.data_category.replace(/_/g, " ")}</span>
              <span className="text-muted-foreground">{r.retention_days} days</span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">Audit log (latest 100)</h2>
        <div className="rounded-2xl border border-border bg-card/60 divide-y divide-border max-h-[500px] overflow-auto">
          {audit.length === 0 && <div className="p-4 text-muted-foreground text-sm">No audit entries yet.</div>}
          {audit.map((a) => (
            <div key={a.id} className="px-4 py-2 text-xs flex justify-between gap-3">
              <span className="font-mono">{a.action_type}</span>
              <span className="text-muted-foreground truncate">
                {a.target_table ?? "—"} · {a.reason ?? ""} · {new Date(a.created_at).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-border bg-card/60 p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-3xl font-heading font-bold">{value}</div>
    </div>
  );
}